import { computed, ref, shallowRef, type Component } from 'vue'

import type { PoiDestination } from '@/components/map/PoiBrowseCard.vue'
import {
  POI_BROWSE_CATEGORIES,
  kindsForCategory,
  type PoiBrowseCategory,
} from '@/config/poiBrowseCategories'

/**
 * Shared state for the gpx.studio-style POI category browser. The selected basemap `kind`s are kept
 * in module scope so the search-panel checklist (`PoiCategoryBrowser`) and the map renderer
 * (`PoiBrowseLayer`) read/write the exact same selection. Session-scoped on purpose — turning POI
 * categories on is a transient browsing action, not a saved preference.
 */
const selectedKinds = ref<Set<string>>(new Set())

/**
 * Everything the POI card needs to render. On desktop `PoiBrowseLayer` renders this into a map popup;
 * on mobile it sets this so `PoiBrowseSheet` shows it as a draggable bottom sheet (so the map stays
 * visible). `shallowRef` because `destinations` carries `add()` closures we don't want proxied.
 */
export interface SelectedPoiCard {
  /** Dedupe key of the corresponding map marker, so it can show an active/selected state. */
  markerKey: string
  lat: number
  lon: number
  name: string
  typeLabel: string
  icon: Component
  iconColor: string
  coordsText: string
  tags: Array<[string, string]>
  hiddenTagCount: number
  destinations: PoiDestination[]
}
const selectedPoiCard = shallowRef<SelectedPoiCard | null>(null)

export function usePoiBrowse() {
  const isCategoryOn = (category: PoiBrowseCategory) =>
    kindsForCategory(category).every((kind) => selectedKinds.value.has(kind))

  const setCategory = (category: PoiBrowseCategory, on: boolean) => {
    const next = new Set(selectedKinds.value)
    kindsForCategory(category).forEach((kind) => (on ? next.add(kind) : next.delete(kind)))
    selectedKinds.value = next
  }

  const toggleCategory = (category: PoiBrowseCategory) =>
    setCategory(category, !isCategoryOn(category))

  const clearAll = () => {
    selectedKinds.value = new Set()
  }

  const selectedCount = computed(
    () => POI_BROWSE_CATEGORIES.filter((category) => isCategoryOn(category)).length,
  )

  const setSelectedPoiCard = (card: SelectedPoiCard | null) => {
    selectedPoiCard.value = card
  }
  const clearSelectedPoiCard = () => {
    selectedPoiCard.value = null
  }

  return {
    selectedKinds,
    isCategoryOn,
    setCategory,
    toggleCategory,
    clearAll,
    selectedCount,
    selectedPoiCard,
    setSelectedPoiCard,
    clearSelectedPoiCard,
  }
}
